/**
 * "Start a new chat for <project>": what a refused project switch, or a
 * refused write to another project, offers (project-switch-refusal.ts). The host creates a chat on that project
 * (`createChatForProject`) and the page goes to it; this chat stays where it
 * is.
 */
import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import { MessageSquarePlus } from 'lucide-react'
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext'
import type { NewChatForProject as Action } from './project-switch-refusal'

export function NewChatForProject({ action }: { action: Action }) {
  const { createChatForProject } = useAppHost()
  const router = useRouter()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const name = action.projectName ?? 'that project'
  if (!createChatForProject) return null
  return (
    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="new-chat-for-project">
      <p>
        {action.boundProjectName
          ? `This chat stays on ${action.boundProjectName}. ${name} needs a chat of its own.`
          : (action.reason ?? `${name} needs a chat of its own.`)}
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setFailed(false)
          const made = await createChatForProject(action.projectId, action.projectName)
          if (made) router.push(made.href)
          else { setFailed(true); setBusy(false) }
        }}
        className="mt-1.5 inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
        data-testid="new-chat-for-project-start"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        Start a new chat for {name}
      </button>
      {failed && <p className="mt-1 text-red-700" data-testid="new-chat-for-project-failed">The new chat could not be started. Try again.</p>}
    </div>
  )
}
